import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, School, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { BrandWord } from "@/components/brand";
import { supabase } from "@/integrations/supabase/client";
import { fetchMyMemberships, requireAuthForOnboarding } from "@/lib/school-context";

export const Route = createFileRoute("/admin/onboarding")({
  ssr: false,
  beforeLoad: () => requireAuthForOnboarding(),
  head: () => ({ meta: [{ title: "Onboard your school — MAKAI" }] }),
  component: OnboardingPage,
});

function OnboardingPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "", district: "", address: "", phone: "", email: "", motto: "",
  });
  const [hasSchool, setHasSchool] = useState(false);

  useEffect(() => {
    fetchMyMemberships().then((m) => {
      const admin = m.filter((x) => x.role === "admin" || x.role === "principal");
      if (admin.length > 0) {
        setHasSchool(true);
        toast.info("You already manage a school.");
        navigate({ to: "/admin" });
      }
    });
  }, [navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("School name is required"); return; }
    setLoading(true);
    const { error } = await supabase.rpc("create_school_with_admin", {
      _name: form.name.trim(),
      _country: "Uganda",
      _district: form.district || undefined,
      _address: form.address || undefined,
      _phone: form.phone || undefined,
      _email: form.email || undefined,
      _motto: form.motto || undefined,
      _currency: "UGX",
      _timezone: "Africa/Kampala",
    });
    setLoading(false);
    if (error) { toast.error(error.message); return; }
    toast.success("School created — welcome to MAKAI Admin!");
    window.dispatchEvent(new CustomEvent("makai:school"));
    navigate({ to: "/admin" });
  }

  if (hasSchool) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-soft via-background to-gold/10 px-4 py-10">
      <div className="mx-auto max-w-xl">
        <div className="mb-8 flex items-center justify-between">
          <BrandWord />
          <button onClick={async () => { await supabase.auth.signOut(); navigate({ to: "/auth" }); }} className="text-xs text-muted-foreground hover:text-foreground">
            Sign out
          </button>
        </div>

        <div className="rounded-3xl border border-border bg-card p-7 shadow-card sm:p-9">
          <div className="grid h-12 w-12 place-items-center rounded-2xl bg-primary-soft text-primary">
            <School className="h-6 w-6" />
          </div>
          <h1 className="mt-4 font-display text-2xl font-bold sm:text-3xl">Set up your school</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Tell us the basics. You'll be added as the school admin and can invite teachers next.
          </p>

          <form className="mt-6 space-y-4" onSubmit={onSubmit}>
            <Field label="School name *">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Kampala Modern Primary" required maxLength={120} />
            </Field>
            <Field label="Motto">
              <Input value={form.motto} onChange={(e) => setForm({ ...form, motto: e.target.value })} placeholder="Optional" maxLength={160} />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="District">
                <Input value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} placeholder="e.g. Kampala" />
              </Field>
              <Field label="Phone">
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+256…" />
              </Field>
            </div>
            <Field label="Email">
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="school@example.org" />
            </Field>
            <Field label="Address">
              <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Street, area, district" rows={2} />
            </Field>
            <Button type="submit" disabled={loading} className="w-full bg-primary hover:bg-primary/90">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ArrowRight className="mr-2 h-4 w-4" />}
              Create school
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
